import { Flex, HStack, Text, useDisclosure } from '@chakra-ui/react';
import debounce from 'lodash.debounce';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { accent, font, neutral, radius, text as textToken } from 'styles/revampTokens';
import InputTypeDataLossWarningModal, {
  needsInputTypeDataLossWarning,
} from 'components/Modals/InputTypeDataLossWarningModal';
import { useAuthContext } from 'context/authContext';
import { getSharedModelUsers } from 'redux/HeaderSlice';
import { getModelTime, setTabShimmer } from 'redux/IndicatorPageSlice';
import { UpdatePlangPageIndicatorTableData, updateSetDriver } from 'redux/PlanPageSlice';
import { getBaseScenario, getPlannerModel } from 'redux/PlannerModeSlice';
import { AppDispatch } from 'utils/GlobalHelpers';

interface Properties {
  inputDimensionsData?: { id: string }[];
  inputType: string;
  indicatorId: number;
  blockId: number;
  refetchDrivers: () => void;
  refetchRequest: () => void;
  inputDataValues: Record<string, string>[];
  timeDimension: number;
  timeRange: string[];
  timeRangeYears: string[];
}

const InputTypeGroupedButton = ({ inputType, indicatorId, refetchRequest }: Properties) => {
  const dispatch: AppDispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('');
  const {
    isOpen: isDataLossWarningOpen,
    onOpen: onDataLossWarningOpen,
    onClose: onDataLossWarningClose,
  } = useDisclosure();
  const [pendingInputType, setPendingInputType] = useState<string | null>(null);
  const modelTime = useSelector(getModelTime);
  const timeGranularity = modelTime?.properties?.time_granularity?.toLowerCase();
  const baseScenario = useSelector(getBaseScenario);
  const [isAPiLoading, setApiLoading] = useState(false);
  const plannerModel = useSelector(getPlannerModel);
  const tabchange = () => {
    if (inputType !== activeTab) {
      setActiveTab(inputType);
    }
  };

  useEffect(() => {
    setActiveTab(inputType);
  }, [inputType]);
  useEffect(() => {
    tabchange();
  }, [activeTab]);

  const { user } = useAuthContext();
  const shareModelUsers = useSelector(getSharedModelUsers);

  const currentUserPermissions = shareModelUsers.find((sharedUser: any) => {
    return user && sharedUser?.user_id === user?.id;
  });

  const disableChange = isAPiLoading || currentUserPermissions?.role === 'view';

  const performInputTypeChange = useCallback(
    async (newType: string) => {
      if (activeTab === newType) return;
      setApiLoading(true);
      const payloadNew: any = {
        indicator_id: indicatorId,
        request_body: {
          type: newType,
          scenario_id: baseScenario?.id,
          bulk_input: true,
          format_excel_data: true,
        },
      };
      setActiveTab(newType);
      dispatch(setTabShimmer(true));
      await dispatch(UpdatePlangPageIndicatorTableData(payloadNew));
      await refetchRequest();
      dispatch(updateSetDriver(newType));
      setTimeout(() => {
        setApiLoading(false);
      }, 600);
    },
    [activeTab, indicatorId, baseScenario?.id, dispatch, refetchRequest],
  );

  const performInputTypeChangeReference = useRef(performInputTypeChange);
  performInputTypeChangeReference.current = performInputTypeChange;

  const debouncedInputTypeChange = useMemo(
    () =>
      debounce((newType: string) => {
        performInputTypeChangeReference.current(newType).catch(() => {});
      }, 1000),
    [],
  );

  useEffect(
    () => () => {
      debouncedInputTypeChange.cancel();
    },
    [debouncedInputTypeChange],
  );

  const requestInputTypeChange = (newType: string) => {
    if (activeTab === newType || disableChange) return;
    if (needsInputTypeDataLossWarning(activeTab, newType)) {
      setPendingInputType(newType);
      onDataLossWarningOpen();
      return;
    }
    debouncedInputTypeChange(newType);
  };

  const handleConfirmDataLossChange = () => {
    if (pendingInputType != null) {
      performInputTypeChange(pendingInputType).catch(() => {});
    }
    setPendingInputType(null);
    onDataLossWarningClose();
  };

  const handleCancelDataLossChange = () => {
    setPendingInputType(null);
    onDataLossWarningClose();
  };

  useEffect(() => {
    if (
      plannerModel?.time_properties?.time_granularity?.toLowerCase() === 'y' &&
      inputType === 'raw'
    ) {
      debouncedInputTypeChange('constant_by_year');
    } else {
      setActiveTab(inputType);
    }
  }, [inputType, plannerModel?.time_properties?.time_granularity, debouncedInputTypeChange]);

  // Monthly only exists when the model itself is monthly — a yearly model has
  // no month-level input to show.
  const segments = [
    { value: 'constant', label: 'Constant' },
    { value: 'constant_by_year', label: 'Yearly' },
    ...(timeGranularity === 'm' ? [{ value: 'raw', label: 'Monthly' }] : []),
    { value: 'growth', label: 'Growth rate' },
  ];

  return (
    <>
      {/* Segmented control in the revamp's language: one neutral track with
          an active segment inside, matching PlanRevamp's time filter. The
          old strip was a solid purple bar with a teal active pill — two
          saturated brand colours doing the job of plain chrome. */}
      <HStack
        spacing='2px'
        align='center'
        w='full'
        flexShrink={0}
        mb={'0'}
        p='2px'
        borderRadius={radius.control}
        borderWidth={1}
        borderColor={neutral.border}
        bg={neutral.surfaceHover}
      >
        {segments.map((segment) => {
          const isActive = activeTab === segment.value;

          return (
            <Flex
              key={segment.value}
              as='button'
              type='button'
              flex='1'
              alignItems='center'
              justifyContent='center'
              h='32px'
              px='8px'
              borderRadius='6px'
              bg={isActive ? neutral.surface : 'transparent'}
              boxShadow={isActive ? '0 1px 3px rgba(16, 20, 30, 0.16)' : 'none'}
              cursor={disableChange ? 'not-allowed' : 'pointer'}
              opacity={disableChange && !isActive ? 0.5 : 1}
              transition='background 0.15s ease, box-shadow 0.15s ease'
              _hover={isActive || disableChange ? undefined : { bg: 'rgba(255,255,255,0.6)' }}
              onClick={() => requestInputTypeChange(segment.value)}
            >
              <Text
                fontFamily={font.body}
                fontSize={{ base: '9px', md: '12px' }}
                fontWeight={isActive ? 700 : 500}
                color={isActive ? accent.solid : textToken.secondary}
                whiteSpace='nowrap'
                lineHeight='1'
              >
                {segment.label}
              </Text>
            </Flex>
          );
        })}
      </HStack>
      <InputTypeDataLossWarningModal
        isOpen={isDataLossWarningOpen}
        onCancel={handleCancelDataLossChange}
        onConfirm={handleConfirmDataLossChange}
      />
    </>
  );
};

export default InputTypeGroupedButton;
